/**
 * Tiny class-based error boundary for individual routes. Renders a useful
 * message + the actual error text so a render-time crash on any single
 * page no longer manifests as a blank white screen.
 *
 * Wrap any page where you'd rather see the error than the blank.
 */

import React from 'react';

interface Props {
  /** Where to send the user when they click "Go back". Defaults to "/". */
  fallbackPath?: string;
  children: React.ReactNode;
}

interface State {
  err: Error | null;
}

export default class RouteErrorBoundary extends React.Component<Props, State> {
  state: State = { err: null };

  static getDerivedStateFromError(err: Error): State {
    return { err };
  }

  componentDidCatch(err: Error, info: React.ErrorInfo) {
    // Surface to console for devtools — also good for Sentry once wired.
    // eslint-disable-next-line no-console
    console.error('[route-error-boundary]', err, info);
  }

  render() {
    if (!this.state.err) return this.props.children;

    const fallback = this.props.fallbackPath ?? '/';
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6">
        <div className="max-w-lg w-full rounded-2xl bg-slate-900/80 backdrop-blur border border-rose-700/60 shadow-2xl p-6">
          <div className="text-[11px] uppercase tracking-wider text-rose-300 font-semibold">Something broke</div>
          <h1 className="text-lg font-bold text-white mt-1">This page hit an error</h1>
          <p className="text-sm text-slate-300 mt-2">
            The page failed to render. The error details are below — share them
            with us if this keeps happening.
          </p>
          <pre className="mt-4 max-h-60 overflow-auto rounded-lg bg-slate-950/80 border border-slate-800 p-3 text-[11px] font-mono text-rose-200 whitespace-pre-wrap break-words">
            {this.state.err.name}: {this.state.err.message}
            {this.state.err.stack ? '\n\n' + this.state.err.stack.split('\n').slice(0, 6).join('\n') : ''}
          </pre>
          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={() => { this.setState({ err: null }); }}
              className="px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-sm text-white transition-all duration-150 active:scale-[0.98]"
            >
              Try again
            </button>
            <a
              href={fallback}
              className="px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-sm text-white font-semibold transition-all duration-150 active:scale-[0.98]"
            >
              Go back
            </a>
          </div>
        </div>
      </div>
    );
  }
}
